import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe,
  Save,
  Loader2,
  User,
  Upload,
  Image,
  Trash2,
  AlertCircle,
  Video,
  Play,
  Users,
  Clock,
  FileSignature,
  ImagePlus,
  Link,
  Shield,
  Volume2,
  X
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import Avatar from '../../components/common/Avatar';

const DAYS_FR = {
  monday: 'Lundi',
  tuesday: 'Mardi',
  wednesday: 'Mercredi',
  thursday: 'Jeudi',
  friday: 'Vendredi',
  saturday: 'Samedi',
  sunday: 'Dimanche'
};

const AgencyInfoSettingsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingSound, setUploadingSound] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const soundInputRef = useRef(null);
  
  const [agencyInfo, setAgencyInfo] = useState({
    name: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'France',
    phone: '',
    email: '',
    website: '',
    logo_url: '',
    login_video_url: '',
    cgv: '',
    email_banner_url: '',
    email_confidentiality_text: '',
    client_portal_url: '',
    account_manager: {
      id: '',
      name: '',
      email: '',
      phone: ''
    },
    // Bank details for wire transfers
    bank_account_holder: '',
    bank_name: '',
    bank_iban: '',
    bank_bic: '',
    // Notification sound
    notification_sound_url: ''
  });

  const [openingHours, setOpeningHours] = useState({
    monday: { morning: { open: '09:00', close: '12:00' }, afternoon: { open: '14:00', close: '18:00' }, closed: false },
    tuesday: { morning: { open: '09:00', close: '12:00' }, afternoon: { open: '14:00', close: '18:00' }, closed: false },
    wednesday: { morning: { open: '09:00', close: '12:00' }, afternoon: { open: '14:00', close: '18:00' }, closed: false },
    thursday: { morning: { open: '09:00', close: '12:00' }, afternoon: { open: '14:00', close: '18:00' }, closed: false },
    friday: { morning: { open: '09:00', close: '12:00' }, afternoon: { open: '14:00', close: '18:00' }, closed: false },
    saturday: { morning: { open: '09:00', close: '12:00' }, afternoon: { open: '', close: '' }, closed: true },
    sunday: { morning: { open: '', close: '' }, afternoon: { open: '', close: '' }, closed: true }
  });

  useEffect(() => {
    fetchAgencyInfo();
    fetchTeamMembers();
    fetchOpeningHours();
  }, []);

  const fetchOpeningHours = async () => {
    try {
      const res = await api.get('/settings/opening-hours');
      if (res.data?.regular) {
        setOpeningHours(res.data.regular);
      } else if (res.data?.hours) {
        setOpeningHours(res.data.hours);
      }
    } catch (error) {
      console.error('Error fetching opening hours:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await api.get('/users/team-members');
      setTeamMembers(res.data || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchAgencyInfo = async () => {
    try {
      const res = await api.get('/settings/agency-info');
      setAgencyInfo({
        name: res.data.name || '',
        address: res.data.address || '',
        postal_code: res.data.postal_code || '',
        city: res.data.city || '',
        country: res.data.country || 'France',
        phone: res.data.phone || '',
        email: res.data.email || '',
        website: res.data.website || '',
        logo_url: res.data.logo_url || '',
        login_video_url: res.data.login_video_url || '',
        account_manager: res.data.account_manager || { id: '', name: '', email: '', phone: '' },
        // Bank details
        bank_account_holder: res.data.bank_account_holder || '',
        bank_name: res.data.bank_name || '',
        bank_iban: res.data.bank_iban || '',
        bank_bic: res.data.bank_bic || '',
        // Notification sound
        notification_sound_url: res.data.notification_sound_url || ''
      });
    } catch (error) {
      console.error('Error fetching agency info:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle notification sound upload
  const handleSoundUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes('audio')) {
      toast.error('Veuillez sélectionner un fichier audio (MP3, WAV)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 2 Mo)');
      return;
    }

    setUploadingSound(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'notification_sound');

    try {
      const res = await api.post('/upload/agency-asset', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.url) {
        setAgencyInfo(prev => ({ ...prev, notification_sound_url: res.data.url }));
        toast.success('Son de notification uploadé');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
      console.error('Upload error:', error);
    } finally {
      setUploadingSound(false);
      if (soundInputRef.current) {
        soundInputRef.current.value = '';
      }
    }
  };

  const playTestSound = () => {
    const soundUrl = agencyInfo.notification_sound_url || '/notification-sound.mp3';
    const audio = new Audio(soundUrl);
    audio.play().catch(err => {
      toast.error('Impossible de jouer le son');
    });
  };

  const handleSelectAccountManager = (memberId) => {
    const member = teamMembers.find(m => m.id === memberId);
    if (member) {
      setAgencyInfo(prev => ({
        ...prev,
        account_manager: {
          id: member.id,
          name: `${member.first_name} ${member.last_name}`,
          email: member.email,
          phone: member.phone || ''
        }
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save agency info
      await api.put('/settings/agency-info', agencyInfo);
      // Save opening hours with the correct format
      await api.put('/settings/opening-hours', { regular: openingHours });
      toast.success('Informations de l\'agence mises à jour !');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setAgencyInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateAccountManager = (field, value) => {
    setAgencyInfo(prev => ({
      ...prev,
      account_manager: { ...prev.account_manager, [field]: value }
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 2 Mo');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'logo_light');

      const response = await api.post('/upload/agency-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.url) {
        setAgencyInfo(prev => ({ ...prev, logo_url: response.data.url }));
        toast.success('Logo uploadé avec succès !');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setAgencyInfo(prev => ({ ...prev, logo_url: '' }));
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 md:px-8 pb-6 md:pb-8",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
              Informations de l'agence
            </h1>
            <p className="text-[#64748b] mt-1">
              Ces informations sont affichées dans le header et sur le site vitrine
            </p>
          </div>
          
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="rounded-full"
            style={{ backgroundColor: theme.colors?.accent }}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Enregistrer
              </>
            )}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Informations générales */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Building2 className="w-5 h-5" />
                  Informations générales
                </CardTitle>
                <CardDescription>Nom et coordonnées de l'agence</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de l'agence</Label>
                  <Input
                    value={agencyInfo.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="MSC L'AGENCE"
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Téléphone
                  </Label>
                  <Input
                    value={agencyInfo.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="+33 1 23 45 67 89"
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    type="email"
                    value={agencyInfo.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="contact@agence.fr"
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Site web
                  </Label>
                  <Input
                    value={agencyInfo.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    placeholder="www.agence.fr"
                    className="input-default"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Adresse */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <MapPin className="w-5 h-5" />
                  Adresse
                </CardTitle>
                <CardDescription>Localisation de l'agence</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input
                    value={agencyInfo.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="12 Rue de l'Agence"
                    className="input-default"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code postal</Label>
                    <Input
                      value={agencyInfo.postal_code}
                      onChange={(e) => updateField('postal_code', e.target.value)}
                      placeholder="75001"
                      className="input-default"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input
                      value={agencyInfo.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      placeholder="Paris"
                      className="input-default"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Input
                    value={agencyInfo.country}
                    onChange={(e) => updateField('country', e.target.value)}
                    placeholder="France"
                    className="input-default"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Coordonnées bancaires (RIB) */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Building2 className="w-5 h-5" />
                  Coordonnées bancaires (RIB)
                </CardTitle>
                <CardDescription>
                  Ces coordonnées seront affichées aux clients pour les paiements par virement
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Titulaire du compte</Label>
                  <Input
                    value={agencyInfo.bank_account_holder}
                    onChange={(e) => updateField('bank_account_holder', e.target.value)}
                    placeholder="Nom du titulaire du compte"
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Banque</Label>
                  <Input
                    value={agencyInfo.bank_name}
                    onChange={(e) => updateField('bank_name', e.target.value)}
                    placeholder="Ex: Crédit Agricole, BNP Paribas..."
                    className="input-default"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={agencyInfo.bank_iban}
                    onChange={(e) => updateField('bank_iban', e.target.value.toUpperCase())}
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    className="input-default font-mono"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>BIC / SWIFT</Label>
                  <Input
                    value={agencyInfo.bank_bic}
                    onChange={(e) => updateField('bank_bic', e.target.value.toUpperCase())}
                    placeholder="Ex: AGRIFRPP"
                    className="input-default font-mono"
                  />
                </div>

                {(!agencyInfo.bank_iban || !agencyInfo.bank_bic) && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      <strong>Important :</strong> Sans ces informations, les clients ne pourront pas effectuer de rechargement par virement bancaire.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Son de notification */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Volume2 className="w-5 h-5" />
                  Son de notification
                </CardTitle>
                <CardDescription>
                  Son joué lors de la réception d&apos;un nouveau message
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm text-slate-600">
                      {agencyInfo.notification_sound_url 
                        ? 'Son personnalisé configuré'
                        : 'Son par défaut utilisé'
                      }
                    </p>
                    {agencyInfo.notification_sound_url && (
                      <p className="text-xs text-slate-400 truncate mt-1">
                        {agencyInfo.notification_sound_url.split('/').pop()}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={playTestSound}
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Tester
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    ref={soundInputRef}
                    type="file"
                    accept="audio/mp3,audio/wav,audio/mpeg"
                    onChange={handleSoundUpload}
                    className="hidden"
                  />
                  <Button 
                    variant="outline"
                    onClick={() => soundInputRef.current?.click()}
                    disabled={uploadingSound}
                    className="flex-1"
                  >
                    {uploadingSound ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Upload...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Uploader un fichier MP3
                      </>
                    )}
                  </Button>
                  
                  {agencyInfo.notification_sound_url && (
                    <Button 
                      variant="ghost"
                      size="sm"
                      onClick={() => setAgencyInfo(prev => ({ ...prev, notification_sound_url: '' }))}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                <p className="text-xs text-slate-500">
                  Formats acceptés : MP3, WAV. Taille max : 2 Mo
                </p>
              </CardContent>
            </Card>

            {/* Logo de l'agence */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Image className="w-5 h-5" />
                  Logo de l&apos;agence
                </CardTitle>
                <CardDescription>
                  Le logo apparaît dans les emails envoyés aux clients et dans les documents générés
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-200 flex items-center justify-center bg-slate-50 overflow-hidden">
                      {agencyInfo.logo_url ? (
                        <img 
                          src={agencyInfo.logo_url} 
                          alt="Logo de l'agence" 
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="text-center p-4">
                          <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                          <p className="text-xs text-slate-400">Aucun logo</p>
                        </div>
                      )}
                    </div>
                    {agencyInfo.logo_url && (
                      <p className="text-xs text-slate-500 mt-2 text-center">Aperçu</p>
                    )}
                  </div>

                  {/* Upload Section */}
                  <div className="flex-1 space-y-4">
                    <div className="space-y-2">
                      <Label>URL du logo ou upload</Label>
                      <Input
                        value={agencyInfo.logo_url}
                        onChange={(e) => updateField('logo_url', e.target.value)}
                        placeholder="https://example.com/logo.png"
                        className="input-default"
                      />
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button 
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingLogo ? 'Upload en cours...' : 'Uploader une image'}
                      </Button>
                      
                      {agencyInfo.logo_url && (
                        <Button 
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={handleRemoveLogo}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
                      <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-blue-600">
                        Format recommandé : PNG ou SVG transparent, dimensions carrées ou horizontales.
                        Le logo sera affiché dans les emails de bienvenue et les contrats générés.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Responsable de compte */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Users className="w-5 h-5" />
                  Responsable de compte par défaut
                </CardTitle>
                <CardDescription>
                  Sélectionnez un membre de l'équipe comme contact par défaut pour les nouveaux clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Member Selection */}
                  <div className="space-y-2">
                    <Label>Sélectionner un membre de l'équipe</Label>
                    <Select 
                      value={agencyInfo.account_manager?.id || ''} 
                      onValueChange={handleSelectAccountManager}
                    >
                      <SelectTrigger className="w-full md:w-96">
                        <SelectValue placeholder="Choisir un membre..." />
                      </SelectTrigger>
                      <SelectContent>
                        {teamMembers.map((member) => (
                          <SelectItem key={member.id} value={member.id}>
                            <div className="flex items-center gap-2">
                              <Avatar 
                                src={member.avatar_url} 
                                name={`${member.first_name} ${member.last_name}`}
                                size="xs"
                                className="w-5 h-5"
                              />
                              {member.first_name} {member.last_name}
                              <span className="text-slate-400 text-xs">({member.email})</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Display selected member info */}
                  {agencyInfo.account_manager?.id && (
                    <div className="p-4 rounded-lg bg-slate-50 border">
                      <p className="text-sm font-medium text-slate-700">Responsable sélectionné :</p>
                      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Nom :</span>
                          <p className="font-medium">{agencyInfo.account_manager.name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Email :</span>
                          <p className="font-medium">{agencyInfo.account_manager.email}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Téléphone :</span>
                          <p className="font-medium">{agencyInfo.account_manager.phone || 'Non renseigné'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Horaires d'ouverture */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Clock className="w-5 h-5" />
                  Horaires d'ouverture
                </CardTitle>
                <CardDescription>
                  Définissez les horaires d'ouverture de l'agence (utilisés sur le site et les emails)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(DAYS_FR).map(([dayKey, dayLabel]) => (
                    <div key={dayKey} className="flex items-center gap-4 py-2 border-b last:border-0">
                      <div className="w-24 font-medium text-slate-700">{dayLabel}</div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!openingHours[dayKey]?.closed}
                          onCheckedChange={(checked) => setOpeningHours(prev => ({
                            ...prev,
                            [dayKey]: { ...prev[dayKey], closed: !checked }
                          }))}
                        />
                        <span className="text-sm text-slate-500 w-16">
                          {openingHours[dayKey]?.closed ? 'Fermé' : 'Ouvert'}
                        </span>
                      </div>
                      {!openingHours[dayKey]?.closed && (
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Matin */}
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-400">Matin:</span>
                            <Input
                              type="time"
                              value={openingHours[dayKey]?.morning?.open || '09:00'}
                              onChange={(e) => setOpeningHours(prev => ({
                                ...prev,
                                [dayKey]: { 
                                  ...prev[dayKey], 
                                  morning: { ...prev[dayKey]?.morning, open: e.target.value }
                                }
                              }))}
                              className="w-24 h-8 text-sm"
                            />
                            <span className="text-slate-400">-</span>
                            <Input
                              type="time"
                              value={openingHours[dayKey]?.morning?.close || '12:00'}
                              onChange={(e) => setOpeningHours(prev => ({
                                ...prev,
                                [dayKey]: { 
                                  ...prev[dayKey], 
                                  morning: { ...prev[dayKey]?.morning, close: e.target.value }
                                }
                              }))}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                          {/* Après-midi */}
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-400">Après-midi:</span>
                            <Input
                              type="time"
                              value={openingHours[dayKey]?.afternoon?.open || '14:00'}
                              onChange={(e) => setOpeningHours(prev => ({
                                ...prev,
                                [dayKey]: { 
                                  ...prev[dayKey], 
                                  afternoon: { ...prev[dayKey]?.afternoon, open: e.target.value }
                                }
                              }))}
                              className="w-24 h-8 text-sm"
                            />
                            <span className="text-slate-400">-</span>
                            <Input
                              type="time"
                              value={openingHours[dayKey]?.afternoon?.close || '18:00'}
                              onChange={(e) => setOpeningHours(prev => ({
                                ...prev,
                                [dayKey]: { 
                                  ...prev[dayKey], 
                                  afternoon: { ...prev[dayKey]?.afternoon, close: e.target.value }
                                }
                              }))}
                              className="w-24 h-8 text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Vidéo de la page de connexion */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Video className="w-5 h-5" />
                  Vidéo de présentation (Page de connexion)
                </CardTitle>
                <CardDescription>
                  Ajoutez une vidéo YouTube en arrière-plan de la page de connexion pour présenter votre agence
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>URL de la vidéo YouTube</Label>
                    <Input
                      value={agencyInfo.login_video_url || ''}
                      onChange={(e) => updateField('login_video_url', e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="input-default"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Formats acceptés : youtube.com/watch?v=xxx ou youtu.be/xxx
                    </p>
                  </div>
                  
                  {/* Preview */}
                  {agencyInfo.login_video_url && (() => {
                    const match = agencyInfo.login_video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
                    const videoId = match ? match[1] : null;
                    
                    if (videoId) {
                      return (
                        <div className="rounded-xl overflow-hidden border border-slate-200">
                          <div className="aspect-video">
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}?autoplay=0&mute=1&controls=1`}
                              title="Aperçu vidéo"
                              className="w-full h-full"
                              allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <Play className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      La vidéo sera lue automatiquement en mode muet sur la page de connexion.
                      Elle remplacera l'arrière-plan coloré de la section gauche.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Conditions Générales de Vente */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <FileSignature className="w-5 h-5" />
                  Conditions Générales de Vente (CGV)
                </CardTitle>
                <CardDescription>
                  Les CGV sont affichées dans les contrats clients et doivent être acceptées lors de la signature
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Texte des CGV</Label>
                    <Textarea
                      value={agencyInfo.cgv || ''}
                      onChange={(e) => updateField('cgv', e.target.value)}
                      placeholder="Entrez vos conditions générales de vente ici...

Exemple:
Article 1 - Objet
Les présentes conditions générales de vente régissent les relations contractuelles entre...

Article 2 - Prix
Les prix sont indiqués en euros et hors taxes..."
                      className="input-default min-h-[300px] font-mono text-sm"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Ce texte apparaîtra dans la section CGV des contrats clients. Les clients devront cocher une case pour accepter ces conditions avant de signer.
                    </p>
                  </div>
                  
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
                    <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-600">
                      <strong>Important :</strong> Assurez-vous que vos CGV respectent la législation en vigueur. 
                      En cas de doute, consultez un professionnel du droit.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Signature Settings */}
            <Card className="card lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ color: theme.colors?.primary }}>
                  <Mail className="w-5 h-5" />
                  Signature des Emails
                </CardTitle>
                <CardDescription>
                  Personnalisez la signature qui apparaîtra en bas de tous les emails envoyés aux clients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Banner Upload */}
                  <div className="space-y-3">
                    <Label className="flex items-center gap-2">
                      <ImagePlus className="w-4 h-4" />
                      Bannière publicitaire (PNG, GIF)
                    </Label>
                    <div className="flex items-start gap-4">
                      <div className="flex-1">
                        <Input
                          value={agencyInfo.email_banner_url || ''}
                          onChange={(e) => updateField('email_banner_url', e.target.value)}
                          placeholder="URL de l'image bannière (ex: https://...)"
                          className="input-default"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Dimensions recommandées : 600x150 pixels. Formats supportés : PNG, GIF, JPG
                        </p>
                      </div>
                      <input
                        type="file"
                        ref={bannerInputRef}
                        className="hidden"
                        accept="image/png,image/gif,image/jpeg"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              const res = await api.post('/upload/agency', formData);
                              updateField('email_banner_url', res.data.url);
                              toast.success('Bannière uploadée');
                            } catch (err) {
                              toast.error("Erreur lors de l'upload");
                            }
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => bannerInputRef.current?.click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                    </div>
                    {agencyInfo.email_banner_url && (
                      <div className="relative inline-block mt-2">
                        <img 
                          src={agencyInfo.email_banner_url} 
                          alt="Bannière email" 
                          className="max-w-md h-auto rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => updateField('email_banner_url', '')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Client Portal URL */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Link className="w-4 h-4" />
                      Lien espace client
                    </Label>
                    <Input
                      value={agencyInfo.client_portal_url || ''}
                      onChange={(e) => updateField('client_portal_url', e.target.value)}
                      placeholder="https://votre-espace-client.com"
                      className="input-default"
                    />
                    <p className="text-xs text-slate-500">
                      Ce lien apparaîtra dans la signature pour permettre aux clients d'accéder à leur espace
                    </p>
                  </div>

                  {/* Confidentiality Text */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Texte de confidentialité
                    </Label>
                    <Textarea
                      value={agencyInfo.email_confidentiality_text || ''}
                      onChange={(e) => updateField('email_confidentiality_text', e.target.value)}
                      placeholder="Ce message et ses pièces jointes sont confidentiels et destinés exclusivement à la personne ou l'entité à laquelle ils sont adressés..."
                      className="input-default"
                      rows={3}
                    />
                    <p className="text-xs text-slate-500">
                      Ce texte apparaît en petit en bas de chaque email. Laissez vide pour utiliser le texte par défaut.
                    </p>
                  </div>

                  {/* Preview */}
                  <div className="p-4 bg-slate-50 rounded-lg border">
                    <p className="text-sm font-medium text-slate-700 mb-3">Aperçu de la signature</p>
                    <div className="bg-white p-4 rounded border">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-slate-200 flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500">
                          {agencyInfo.logo_url ? (
                            <img src={agencyInfo.logo_url} alt="Logo" className="w-12 h-12 object-contain" />
                          ) : (
                            <span className="text-white font-bold">SC</span>
                          )}
                        </div>
                        <div className="text-sm">
                          <p className="font-semibold text-slate-800">Service Client</p>
                          <p className="text-slate-500 text-xs">Votre service com'</p>
                          <p className="text-slate-600 text-xs mt-2">📞 {agencyInfo.phone || '04 XX XX XX XX'}</p>
                          <p className="text-slate-600 text-xs">📧 {agencyInfo.email || 'contact@agence.fr'}</p>
                          <p className="text-slate-600 text-xs">🌐 {agencyInfo.website || 'agence.fr'}</p>
                          <p className="text-emerald-600 text-xs">🔗 Accéder à mon espace client</p>
                        </div>
                      </div>
                      {agencyInfo.email_banner_url && (
                        <div className="mt-4">
                          <img src={agencyInfo.email_banner_url} alt="Bannière" className="max-w-full h-auto rounded" />
                        </div>
                      )}
                      <p className="text-[10px] text-slate-400 mt-4 pt-3 border-t">
                        {agencyInfo.email_confidentiality_text || "Ce message et ses pièces jointes sont confidentiels..."}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AgencyInfoSettingsPage;
